import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { TFunction } from 'i18next';

import AdaChatbot from '@talend/ada-chatbot';
import {
	scim,
	ScimAccount,
	ScimMe,
	TalendWebappConfiguration,
	webappConfiguration,
} from '@talend/context';
import { useFlag, useOpenLabsButton } from '@talend/feature-flipping';
import { useDocLink } from '@talend/helplink';
import InProductChat from '@talend/in-product-chat';
import { ConnectedNotificationBell } from '@talend/notification-center';
import { HeaderBar } from '@talend/react-containers';

import { FEATURE_FLAGS, I18N_NAME_SPACE } from '../../../constants';
import { isTrialAccount } from '../../../utils/account.cache';

function getIPCComponent(
	account: ScimAccount,
	me: ScimMe,
	webappConfig: TalendWebappConfiguration,
	t: TFunction,
	adaChatbotEnabled: boolean,
) {
	if (!account || account.subscription.options.ipc_enabled === 'false') {
		return undefined;
	}

	if (adaChatbotEnabled) {
		return AdaChatbot;
	}

	const intercomAppId = webappConfig.intercomAppId;

	if (!me || !intercomAppId) {
		return undefined;
	}

	return () => (
		<InProductChat
			intercomId={intercomAppId}
			appId="tdc-intercom"
			account={account}
			meProfile={me}
			tooltipPlacement="bottom"
			t={t}
		/>
	);
}

function getUserMenu(
	me: ScimMe,
	portalUrl: string,
	t: TFunction,
	onClickAbout: () => void,
	onClickGuidedTour: () => void,
	isGuidedTourDisabled: boolean,
	labButton: {
		id: string;
		'data-testid': string;
		label: string;
		onClick: () => void;
	} | null,
) {
	return {
		id: 'talend-user',
		label: scim.me.getDisplayName(me),
		items: [
			{
				id: 'help:about',
				label: t('HEADERBAR_HELP_ABOUT', 'About Data Inventory'),
				onClick: onClickAbout,
			},
			!isGuidedTourDisabled && {
				id: 'userMenu_guidedTour',
				label: t('HEADERBAR_HELP_GUIDED_TOUR', 'Guided tour'),
				onClick: onClickGuidedTour,
			},
			{
				divider: true,
			},
			{
				id: 'userMenu_support',
				label: t('HEADERBAR_HELP_SUPPORT', 'Support'),
				target: '_blank',
				rel: 'noopener noreferrer',
				href: 'https://community.qlik.com/t5/Support/ct-p/qlikSupport',
			},
			{
				id: 'userMenu_community',
				label: t('HEADERBAR_HELP_COMMUNITY', 'Community'),
				target: '_blank',
				rel: 'noopener noreferrer',
				href: 'https://community.talend.com/s/group/0F93p0000000VOACA2/data-and-metadata-governance',
				'data-feature': 'header.menu.community',
			},
			{
				divider: true,
			},
			labButton,
			{
				id: 'userMenu_profilePreferences',
				label: t('PROFILE_PREFERENCES', 'Profile preferences'),
				href: `${portalUrl}/user`,
			},
			{
				id: 'user-logout',
				label: t('LOGOUT', 'Logout'),
				href: '/signOut',
			},
		].filter(Boolean),
	};
}

export interface HeaderProps {
	onClickAbout: () => void;
	onClickGuidedTour: () => void;
}

export function Header({ onClickGuidedTour, onClickAbout }: HeaderProps): React.ReactElement {
	const [helpUrl] = useDocLink('inv_help');
	const adaChatbotEnabled = useFlag(FEATURE_FLAGS.ADA_CHATBOT_ENABLED);
	const account = scim.account.getCache();
	const webappConfig = webappConfiguration.getCache();
	const me = scim.me.getCache();
	const { t } = useTranslation(I18N_NAME_SPACE);

	const labButton = useOpenLabsButton();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const GetIntercomComponent = React.useMemo(
		() => getIPCComponent(account, me, webappConfig, t, adaChatbotEnabled),
		[t, adaChatbotEnabled],
	);

	const isTalendExperienceEnabled = isTrialAccount();

	return (
		<HeaderBar
			brand={{
				label: 'Data Inventory',
				id: 'app_switcherApps',
				icon: 'talend-tdc-negative',
			}}
			genericAction={
				isTalendExperienceEnabled && {
					id: 'header-generic-action',
					icon: 'talend-info-circle',
					label: t('TALEND_EXPERIENCE', 'Talend Experience'),
					onClick: () => window.open(webappConfig.talendExperienceUrl, '_blank'),
					'data-testid': 'headerbar.discovery-link',
				}
			}
			products={{ authorizedProducts: me.applications }}
			logo={{ isFull: false, href: webappConfig.portalPublicUrl, id: 'app_talendLogo' }}
			help={{
				id: 'talend-documentation',
				target: '_blank',
				rel: 'noopener noreferrer',
				href: helpUrl,
			}}
			user={getUserMenu(
				me,
				webappConfig.portalPublicUrl,
				t,
				onClickAbout,
				onClickGuidedTour,
				isTalendExperienceEnabled,
				labButton,
			)}
			Intercom={GetIntercomComponent}
			NotificationCenter={() => <ConnectedNotificationBell />}
		/>
	);
}
